import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, Divider, Tabs, Tab, Table, TableHead,
  TableRow, TableCell, LinearProgress, Switch, FormControlLabel,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  HONEYMOON_PARTNERS_QUERY,
  HONEYMOON_PACKAGES_QUERY,
  CREATE_HONEYMOON_PARTNER_MUTATION,
  UPDATE_HONEYMOON_PARTNER_MUTATION,
  DELETE_HONEYMOON_PARTNER_MUTATION,
  CREATE_HONEYMOON_PACKAGE_MUTATION,
  UPDATE_HONEYMOON_PACKAGE_MUTATION,
  DELETE_HONEYMOON_PACKAGE_MUTATION,
} from '../graphql/queries';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const PARTNER_TYPES   = ['travel_agency', 'hotel_chain', 'airline', 'resort', 'cruise_line', 'other'];
const PACKAGE_TYPES   = ['budget', 'standard', 'premium', 'luxury'];
const PARTNER_TYPE_ICON = {
  travel_agency: <TravelExploreIcon fontSize="small" />,
  hotel_chain:   <HotelIcon fontSize="small" />,
  airline:       <FlightIcon fontSize="small" />,
  resort:        <HotelIcon fontSize="small" />,
  cruise_line:   <DirectionsBoatIcon fontSize="small" />,
  other:         <TravelExploreIcon fontSize="small" />,
};
const PKG_TYPE_COLOR = { budget: 'default', standard: 'info', premium: 'secondary', luxury: 'warning' };

const EMPTY_PARTNER = { name: '', partnerType: 'travel_agency', description: '', website: '', phone: '', email: '', destinations: '' };
const EMPTY_PKG     = { partnerId: '', name: '', destination: '', durationDays: 5, basePrice: '', packageType: 'standard', inclusions: '', roomTypes: '', description: '' };

const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

// ─────────────────────────────────────────────────────────────────
// PARTNER FORM DIALOG
// ─────────────────────────────────────────────────────────────────
function PartnerDialog({ open, onClose, editPartner, refetch }) {
  const [form, setForm]   = useState(EMPTY_PARTNER);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (editPartner) {
      setForm({
        name:        editPartner.name,
        partnerType: editPartner.partnerType || 'travel_agency',
        description: editPartner.description || '',
        website:     editPartner.website || '',
        phone:       editPartner.phone || '',
        email:       editPartner.email || '',
        destinations: (editPartner.destinations || []).join(', '),
      });
    } else {
      setForm(EMPTY_PARTNER);
    }
    setError('');
  }, [editPartner, open]);

  const [createPartner] = useMutation(CREATE_HONEYMOON_PARTNER_MUTATION, {
    onCompleted: () => { onClose(); refetch(); },
    onError: e => setError(e.message),
  });
  const [updatePartner] = useMutation(UPDATE_HONEYMOON_PARTNER_MUTATION, {
    onCompleted: () => { onClose(); refetch(); },
    onError: e => setError(e.message),
  });

  const handleSave = () => {
    const dests = form.destinations ? form.destinations.split(',').map(d => d.trim()).filter(Boolean) : [];
    const vars  = { ...form, destinations: dests };
    if (editPartner) updatePartner({ variables: { id: editPartner.id, ...vars } });
    else createPartner({ variables: vars });
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editPartner ? 'Edit Partner' : 'Add Honeymoon Partner'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Partner Name" value={form.name} onChange={set('name')} fullWidth required autoFocus placeholder="e.g. Make My Trip, Taj Hotels" />
            <TextField select label="Type" value={form.partnerType} onChange={set('partnerType')} sx={{ minWidth: 160 }}>
              {PARTNER_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label="Description" value={form.description} onChange={set('description')} fullWidth multiline rows={2} placeholder="Brief description of services offered" />
          <Stack direction="row" spacing={2}>
            <TextField label="Phone" value={form.phone} onChange={set('phone')} fullWidth placeholder="+91 98765 43210" />
            <TextField label="Email" value={form.email} onChange={set('email')} fullWidth type="email" />
          </Stack>
          <TextField label="Website" value={form.website} onChange={set('website')} fullWidth placeholder="https://www.example.com" />
          <TextField
            label="Destinations (comma-separated)"
            value={form.destinations}
            onChange={set('destinations')}
            fullWidth
            placeholder="e.g. Maldives, Bali, Paris, Kerala"
            helperText="Enter destination names separated by commas"
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────
// PACKAGE FORM DIALOG
// ─────────────────────────────────────────────────────────────────
function PackageDialog({ open, onClose, editPkg, partners, refetch }) {
  const [form, setForm]   = useState(EMPTY_PKG);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (editPkg) {
      setForm({
        partnerId:   editPkg.partner?.id || '',
        name:        editPkg.name,
        destination: editPkg.destination,
        durationDays: editPkg.durationDays || 5,
        basePrice:   editPkg.basePrice || '',
        packageType: editPkg.packageType || 'standard',
        inclusions:  (editPkg.inclusions || []).join(', '),
        roomTypes:   (editPkg.roomTypes || []).join(', '),
        description: editPkg.description || '',
      });
    } else {
      setForm(EMPTY_PKG);
    }
    setError('');
  }, [editPkg, open]);

  const [createPkg] = useMutation(CREATE_HONEYMOON_PACKAGE_MUTATION, {
    onCompleted: () => { onClose(); refetch(); },
    onError: e => setError(e.message),
  });
  const [updatePkg] = useMutation(UPDATE_HONEYMOON_PACKAGE_MUTATION, {
    onCompleted: () => { onClose(); refetch(); },
    onError: e => setError(e.message),
  });

  const handleSave = () => {
    const inclusions = form.inclusions ? form.inclusions.split(',').map(s => s.trim()).filter(Boolean) : [];
    const roomTypes  = form.roomTypes  ? form.roomTypes.split(',').map(s => s.trim()).filter(Boolean)  : [];
    const vars = {
      ...form,
      inclusions,
      roomTypes,
      durationDays: Number(form.durationDays) || 5,
      basePrice:    form.basePrice ? Number(form.basePrice) : null,
    };
    if (editPkg) updatePkg({ variables: { id: editPkg.id, ...vars } });
    else createPkg({ variables: vars });
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editPkg ? 'Edit Package' : 'Add Honeymoon Package'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField select label="Partner" value={form.partnerId} onChange={set('partnerId')} fullWidth required>
              <MenuItem value="">Select a Partner</MenuItem>
              {partners.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <TextField select label="Package Type" value={form.packageType} onChange={set('packageType')} sx={{ minWidth: 140 }}>
              {PACKAGE_TYPES.map(t => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Package Name" value={form.name} onChange={set('name')} fullWidth required autoFocus placeholder="e.g. Maldives Overwater Bliss" />
            <TextField label="Destination" value={form.destination} onChange={set('destination')} fullWidth required placeholder="e.g. Maldives" />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Duration (nights)" type="number" value={form.durationDays} onChange={set('durationDays')} fullWidth />
            <TextField label="Base Price (₹)" type="number" value={form.basePrice} onChange={set('basePrice')} fullWidth placeholder="Price per couple" />
          </Stack>
          <TextField
            label="Inclusions (comma-separated)"
            value={form.inclusions}
            onChange={set('inclusions')}
            fullWidth
            placeholder="e.g. Flights, Hotel, Breakfast, Airport Transfer"
            helperText="What's included in this package"
          />
          <TextField
            label="Room Types (comma-separated)"
            value={form.roomTypes}
            onChange={set('roomTypes')}
            fullWidth
            placeholder="e.g. Standard, Deluxe, Suite, Overwater Villa"
          />
          <TextField label="Description" value={form.description} onChange={set('description')} fullWidth multiline rows={2} placeholder="Detailed description of the package" />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!form.name.trim() || !form.destination.trim() || !form.partnerId}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────
export default function AdminHoneymoonPage() {
  const [tab, setTab] = useState(0);

  // Partner state
  const [partnerDialog, setPartnerDialog] = useState(false);
  const [editPartner, setEditPartner]     = useState(null);

  // Package state
  const [pkgDialog, setPkgDialog] = useState(false);
  const [editPkg, setEditPkg]     = useState(null);

  const { data: partnerData, loading: partnerLoading, refetch: refetchPartners } = useQuery(HONEYMOON_PARTNERS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: pkgData, loading: pkgLoading, refetch: refetchPkgs }             = useQuery(HONEYMOON_PACKAGES_QUERY, { fetchPolicy: 'cache-and-network' });

  const partners = partnerData?.honeymoonPartners || [];
  const packages = pkgData?.honeymoonPackages     || [];

  const [deletePartner] = useMutation(DELETE_HONEYMOON_PARTNER_MUTATION, { onCompleted: () => refetchPartners() });
  const [deletePkg]     = useMutation(DELETE_HONEYMOON_PACKAGE_MUTATION, { onCompleted: () => refetchPkgs() });

  const [updatePartner] = useMutation(UPDATE_HONEYMOON_PARTNER_MUTATION, { onCompleted: () => refetchPartners() });

  const togglePartnerActive = (p) => updatePartner({ variables: { id: p.id, isActive: !p.isActive } });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Honeymoon Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage travel partners and honeymoon packages for couples.</Typography>
        </Box>
        {tab === 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditPartner(null); setPartnerDialog(true); }}>
            Add Partner
          </Button>
        )}
        {tab === 1 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditPkg(null); setPkgDialog(true); }}
            disabled={partners.length === 0}>
            Add Package
          </Button>
        )}
      </Stack>

      <ContractedVendorsSection category="honeymoon" />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Travel Partners (${partners.length})`} />
        <Tab label={`Honeymoon Packages (${packages.length})`} />
      </Tabs>

      {/* ── TAB 0: Travel Partners ── */}
      {tab === 0 && (
        <Box>
          {partnerLoading && <LinearProgress sx={{ mb: 2 }} />}
          {partners.length === 0 && !partnerLoading && (
            <Alert severity="info">No honeymoon partners yet. Click <strong>Add Partner</strong> to add travel agencies, hotels, and airlines.</Alert>
          )}

          <Grid container spacing={2}>
            <Paged items={partners}>{rows => rows.map(p => (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card sx={{ opacity: p.isActive ? 1 : 0.6 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {PARTNER_TYPE_ICON[p.partnerType] || PARTNER_TYPE_ICON.other}
                        <Typography fontWeight={700}>{p.name}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => { setEditPartner(p); setPartnerDialog(true); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error"
                            onClick={() => window.confirm(`Delete partner "${p.name}"?`) && deletePartner({ variables: { id: p.id } })}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Chip label={p.partnerType?.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ mb: 1 }} />

                    {p.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                        {p.description.length > 80 ? p.description.slice(0, 80) + '...' : p.description}
                      </Typography>
                    )}

                    {p.destinations?.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {p.destinations.slice(0, 4).map(d => (
                          <Chip key={d} label={d} size="small" sx={{ mr: 0.5, mb: 0.5 }} color="primary" variant="outlined" />
                        ))}
                        {p.destinations.length > 4 && <Chip label={`+${p.destinations.length - 4} more`} size="small" />}
                      </Box>
                    )}

                    <Stack spacing={0.3}>
                      {p.phone   && <Typography variant="caption" color="text.secondary">Phone: {p.phone}</Typography>}
                      {p.email   && <Typography variant="caption" color="text.secondary">Email: {p.email}</Typography>}
                      {p.website && <Typography variant="caption" color="text.secondary">Web: {p.website}</Typography>}
                    </Stack>

                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Packages: {packages.filter(pkg => pkg.partner?.id === p.id).length}
                      </Typography>
                      <FormControlLabel
                        control={<Switch size="small" checked={!!p.isActive} onChange={() => togglePartnerActive(p)} />}
                        label={<Typography variant="caption">{p.isActive ? 'Active' : 'Inactive'}</Typography>}
                        sx={{ m: 0 }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* ── TAB 1: Honeymoon Packages ── */}
      {tab === 1 && (
        <Box>
          {partners.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>Add at least one travel partner first before creating packages.</Alert>
          )}
          {pkgLoading && <LinearProgress sx={{ mb: 2 }} />}
          {packages.length === 0 && !pkgLoading && (
            <Alert severity="info">No honeymoon packages yet. Click <strong>Add Package</strong> to create destination packages.</Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                {['Package Name', 'Partner', 'Destination', 'Duration', 'Type', 'Base Price', 'Inclusions', 'Room Types', 'Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map(pkg => (
                <TableRow key={pkg.id} hover sx={{ opacity: pkg.isActive ? 1 : 0.55 }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{pkg.name}</Typography>
                    {pkg.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {pkg.description.length > 50 ? pkg.description.slice(0, 50) + '…' : pkg.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {PARTNER_TYPE_ICON[pkg.partner?.partnerType] || PARTNER_TYPE_ICON.other}
                      <Typography variant="body2">{pkg.partner?.name || '—'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{pkg.destination}</TableCell>
                  <TableCell>{pkg.durationDays ? `${pkg.durationDays}N` : '—'}</TableCell>
                  <TableCell><Chip label={pkg.packageType} size="small" color={PKG_TYPE_COLOR[pkg.packageType] || 'default'} /></TableCell>
                  <TableCell>{fmt(pkg.basePrice)}</TableCell>
                  <TableCell>
                    {(pkg.inclusions || []).slice(0, 3).map(inc => (
                      <Chip key={inc} label={inc} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                    {(pkg.inclusions || []).length > 3 && <Chip label={`+${pkg.inclusions.length - 3}`} size="small" />}
                  </TableCell>
                  <TableCell>
                    {(pkg.roomTypes || []).map(rt => (
                      <Chip key={rt} label={rt} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.3 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip label={pkg.isActive ? 'Active' : 'Inactive'} size="small" color={pkg.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => { setEditPkg(pkg); setPkgDialog(true); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error"
                          onClick={() => window.confirm(`Delete package "${pkg.name}"?`) && deletePkg({ variables: { id: pkg.id } })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Dialogs */}
      <PartnerDialog
        open={partnerDialog}
        onClose={() => { setPartnerDialog(false); setEditPartner(null); }}
        editPartner={editPartner}
        refetch={refetchPartners}
      />
      <PackageDialog
        open={pkgDialog}
        onClose={() => { setPkgDialog(false); setEditPkg(null); }}
        editPkg={editPkg}
        partners={partners}
        refetch={refetchPkgs}
      />
    </Box>
  );
}
